
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Slider from "@mui/material/Slider";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";

// Switch Control
export function SettingsSwitch({
  label,
  checked,
  onChange,
  description,
  disabled,
}) {
  return (
    <Box>
      <FormControlLabel
        control={
          <Switch
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "#2932E5",
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#2932E5",
              },
            }}
          />
        }
        label={
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: 14,
              color: "#212121",
            }}
          >
            {label}
          </Typography>
        }
        sx={{ m: 0 }}
      />
      {description && (
        <Typography
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: 12,
            color: "#4A5565",
            ml: 6.5,
            mt: 0.5,
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
}

// Select Dropdown Control
export function SettingsSelect({
  label,
  value,
  onChange,
  options,
  description,
  disabled,
  error,
}) {
  return (
    <FormControl fullWidth size="small" error={!!error} disabled={disabled}>
      <InputLabel
        sx={{
          fontFamily: "Roboto, sans-serif",
          fontSize: 14,
          color: "#4A5565",
          "&.Mui-focused": {
            color: "#2932E5",
          },
        }}
      >
        {label}
      </InputLabel>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        label={label}
        sx={{
          fontFamily: "Roboto, sans-serif",
          fontSize: 14,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#E5E7EB",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#2932E5",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#2932E5",
          },
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {(description || error) && (
        <FormHelperText
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: 12,
            color: error ? "#D32F2F" : "#4A5565",
          }}
        >
          {error || description}
        </FormHelperText>
      )}
    </FormControl>
  );
}

// Slider Control
export function SettingsSlider({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  marks,
  description,
  disabled,
  valueDisplay,
}) {
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: 14,
            color: "#212121",
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: 14,
            fontWeight: 500,
            color: "#2932E5",
          }}
        >
          {valueDisplay ? valueDisplay(value) : value}
        </Typography>
      </Box>
      <Slider
        value={value}
        onChange={(_, newValue) => onChange(newValue)}
        min={min}
        max={max}
        step={step}
        marks={marks}
        disabled={disabled}
        sx={{
          color: "#2932E5",
          "& .MuiSlider-thumb": {
            "&:hover, &.Mui-focusVisible": {
              boxShadow: "0 0 0 8px rgba(41, 50, 229, 0.16)",
            },
          },
        }}
      />
      {description && (
        <Typography
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: 12,
            color: "#4A5565",
            mt: 0.5,
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
}

// Number Input Control
export function SettingsNumberInput({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  description,
  disabled,
  error,
  suffix,
}) {
  const handleChange = (e) => {
    const newValue = parseFloat(e.target.value);
    if (!isNaN(newValue)) {
      if (min !== undefined && newValue < min) return;
      if (max !== undefined && newValue > max) return;
      onChange(newValue);
    }
  };

  return (
    <TextField
      label={label}
      type="number"
      value={value}
      onChange={handleChange}
      size="small"
      fullWidth
      disabled={disabled}
      error={!!error}
      helperText={error || description}
      InputProps={{
        endAdornment: suffix ? (
          <Typography sx={{ fontSize: 14, color: "#4A5565", ml: 1 }}>
            {suffix}
          </Typography>
        ) : undefined,
      }}
      inputProps={{
        min,
        max,
        step,
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          fontFamily: "Roboto, sans-serif",
          fontSize: 14,
          "& fieldset": {
            borderColor: "#E5E7EB",
          },
          "&:hover fieldset": {
            borderColor: "#2932E5",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#2932E5",
          },
        },
        "& .MuiInputLabel-root": {
          fontFamily: "Roboto, sans-serif",
          fontSize: 14,
          color: "#4A5565",
          "&.Mui-focused": {
            color: "#2932E5",
          },
        },
        "& .MuiFormHelperText-root": {
          fontFamily: "Roboto, sans-serif",
          fontSize: 12,
        },
      }}
    />
  );
}
